import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Users, PawPrint, Flame, Sparkles } from "lucide-react";

export default function Companions() {
  const [companions, setCompanions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [species, setSpecies] = useState("All"); // All | Human | Beastkin
  const [trope, setTrope] = useState("All");

  useEffect(() => {
    let cancelled = false;
    async function load(attempt = 0) {
      try {
        const r = await api.get("/companions");
        if (cancelled) return;
        setCompanions(r.data || []);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        // Retry once on abort / network error — anonymous visitors sometimes
        // see the first in-flight request cancelled by the SW controller swap.
        if (attempt === 0) return load(1);
        setError(e.message || "Could not load companions");
        setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const tropes = useMemo(() => {
    const set = new Set(["All"]);
    for (const c of companions) if (c.trope) set.add(c.trope);
    return Array.from(set);
  }, [companions]);

  const visible = useMemo(() => {
    return companions.filter((c) => {
      if (species !== "All" && (c.species || "Beastkin") !== species) return false;
      if (trope !== "All" && c.trope !== trope) return false;
      return true;
    });
  }, [companions, species, trope]);

  const Chip = ({ active, onClick, children, testid }) => (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full border h-8 px-3 text-[11px] font-mono uppercase tracking-widest transition-colors ${
        active
          ? "bg-amber-500 border-amber-400 text-black"
          : "bg-[#07080A] border-white/10 text-slate-400 hover:border-amber-400/60 hover:text-amber-300"
      }`}
    >
      {children}
    </button>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-16">
      <div className="mb-8">
        <div className="font-mono text-[11px] uppercase tracking-[0.24em] text-amber-400 mb-2">
          Companions · live AI roleplay
        </div>
        <h1 className="font-serif text-3xl md:text-5xl font-bold tracking-tight">
          Who are you talking to tonight?
        </h1>
        <p className="mt-3 text-slate-400 max-w-2xl">
          Humans with history. Beastkin with fangs. Pick one and the story bends to you.
        </p>
      </div>

      <div className="mb-5">
        <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1.5">
          <Users className="h-3 w-3 text-amber-400" /> Species
        </div>
        <div className="flex flex-wrap gap-2">
          <Chip testid="species-chip-all" active={species === "All"} onClick={() => setSpecies("All")}>
            <Sparkles className="h-3 w-3" /> All
          </Chip>
          <Chip testid="species-chip-human" active={species === "Human"} onClick={() => setSpecies("Human")}>
            <Users className="h-3 w-3" /> Human
          </Chip>
          <Chip testid="species-chip-beastkin" active={species === "Beastkin"} onClick={() => setSpecies("Beastkin")}>
            <PawPrint className="h-3 w-3" /> Beastkin
          </Chip>
        </div>
      </div>

      <div className="mb-6">
        <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1.5">
          <Flame className="h-3 w-3 text-rose-400" /> Trope
        </div>
        <div className="flex flex-wrap gap-2">
          {tropes.map((t) => (
            <Chip
              key={t}
              testid={`trope-chip-${t.toLowerCase().replace(/\s+/g, "-")}`}
              active={trope === t}
              onClick={() => setTrope(t)}
            >
              {t}
            </Chip>
          ))}
        </div>
      </div>

      {loading ? (
        <div data-testid="companions-loading" className="rounded-3xl border border-white/10 bg-[#0F1117] p-10 text-center text-slate-500 text-sm font-mono uppercase tracking-widest">
          Loading companions…
        </div>
      ) : error ? (
        <div data-testid="companions-error" className="rounded-3xl border border-rose-500/40 bg-rose-500/5 p-10 text-center text-rose-200 text-sm">
          Couldn't load the cast — <button onClick={() => window.location.reload()} className="underline hover:text-rose-100">try reloading</button>.
        </div>
      ) : visible.length === 0 ? (
        <div data-testid="companions-empty" className="rounded-3xl border border-white/10 bg-[#0F1117] p-10 text-center text-slate-400 text-sm">
          No companions match this filter. Try broadening it.
        </div>
      ) : (
        <div
          data-testid="companions-grid"
          className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
        >
          {visible.map((c) => (
            <Link
              key={c.companion_id}
              to={`/companions/${c.companion_id}`}
              data-testid={`companions-card-${c.companion_id}`}
              className="group rounded-2xl overflow-hidden border border-white/10 bg-[#0F1117] hover:border-amber-400/40 transition-colors"
            >
              <div className="aspect-[3/4] overflow-hidden relative bg-black/50">
                <img
                  src={c.generated_portrait || c.image_url}
                  alt={c.name}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#07080A] via-transparent to-transparent" />
                <div className="absolute top-2 left-2 flex gap-1">
                  <span className="inline-flex items-center gap-1 rounded-full border border-amber-400/40 bg-black/70 backdrop-blur px-2 py-0.5 text-[9px] font-mono uppercase tracking-widest text-amber-200">
                    {c.species || "Beastkin"}
                  </span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <div className="font-mono text-[9px] uppercase tracking-widest text-slate-400">{c.trope}</div>
                  <div className="font-serif text-xl text-white font-bold leading-tight">{c.name}</div>
                </div>
              </div>
              <div className="p-3">
                <p className="text-xs text-slate-400 italic line-clamp-2">"{c.tagline}"</p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
